/**
 * V4.41 — Bouton ✉️ Email en 1 clic.
 *
 * Doctrine : dans 90 % des cas, Crédit Flow doit choisir le bon modèle et
 * ouvrir le brouillon directement. L'utilisateur ne doit intervenir que s'il
 * veut changer de modèle.
 *
 * Comportement :
 *   1. Clic sur la zone principale (✉️ + "Email") → ouvre Gmail avec le
 *      template suggéré (le premier de la liste compatible)
 *   2. Clic sur le caret ▾ secondaire → ouvre le dropdown "Changer de modèle"
 *   3. La relance N'EST PAS marquée comme traitée (doctrine : fiabilité > vitesse)
 *   4. Le tracking analytics est conservé sur les deux chemins
 */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { EnvelopeSimple, CaretDown } from "@phosphor-icons/react";
import {
  getCompatibleTemplates,
  buildEmail,
  openGmailDraft,
} from "@/lib/emailTemplates";
import { track, USAGE_EVENTS } from "@/lib/usage";

const AUDIENCE_PALETTE = {
  client: { bg: "var(--cf-primary-soft)", text: "var(--cf-primary-hover)", hover: "var(--cf-primary-soft)", border: "var(--cf-primary-border)" },
  banque: { bg: "var(--cf-success-soft)", text: "var(--cf-success-text)", hover: "var(--cf-success-border)", border: "var(--cf-success-border)" },
  bos: { bg: "var(--cf-accent-soft)", text: "var(--cf-accent-text)", hover: "var(--cf-accent-soft)", border: "var(--cf-accent-soft)" },
  apporteur: { bg: "var(--cf-warning-soft)", text: "var(--cf-warning-text)", hover: "var(--cf-warning-soft)", border: "var(--cf-warning-border)" },
};

export default function EmailRelanceButton({ audience, dossier, compact = false, testid }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Fermer le dropdown si clic en dehors
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const templates = getCompatibleTemplates(audience, dossier);
  if (!templates.length) return null;

  const palette = AUDIENCE_PALETTE[audience] || AUDIENCE_PALETTE.client;
  const suggested = templates[0];
  const baseTestId = testid || `email-relance-${audience}`;

  const sendTemplate = (template) => {
    const payload = buildEmail(template, dossier, user, audience);
    openGmailDraft(payload);
    track(USAGE_EVENTS.COMMANDO_EMAIL, {
      dossier_id: dossier?.id,
      etape: dossier?.etape,
      meta: { template: template.id, audience },
    });
  };

  // V4.41 — Clic principal : envoi direct du template suggéré.
  // Fonctionne aussi si client_email est vide (Gmail s'ouvre avec to= vide).
  const handlePrimaryClick = (e) => {
    e.stopPropagation();
    sendTemplate(suggested);
  };

  // V4.41 — Caret ▾ : ouvre le menu "Changer de modèle"
  const handleCaretClick = (e) => {
    e.stopPropagation();
    setOpen((v) => !v);
  };

  const handleTemplateClick = (template) => {
    sendTemplate(template);
    setOpen(false);
  };

  const sizePx = compact ? 12 : 14;
  const caretPx = compact ? 10 : 12;
  const padPrimary = compact ? "px-2 py-1 text-[11px]" : "px-2.5 py-1.5 text-[12px]";
  const padCaret = compact ? "px-1 py-1" : "px-1.5 py-1.5";

  return (
    <div
      ref={wrapRef}
      className="relative inline-flex"
      data-testid={baseTestId}
    >
      {/* Groupe split-button : primaire + caret */}
      <div
        className="inline-flex items-stretch rounded-lg overflow-hidden"
        style={{ background: palette.bg }}
      >
        {/* Bouton principal — déclenche directement le template suggéré */}
        <button
          type="button"
          onClick={handlePrimaryClick}
          className={`inline-flex items-center gap-1 font-extrabold transition-colors ${padPrimary}`}
          style={{ color: palette.text }}
          onMouseEnter={(e) => (e.currentTarget.style.background = palette.hover)}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          title={`Email — ${suggested.label}`}
          data-testid={`${baseTestId}-btn`}
        >
          <EnvelopeSimple size={sizePx} weight="fill" />
          {!compact && <span>Email</span>}
        </button>

        {/* Séparateur visuel fin */}
        <div
          aria-hidden="true"
          style={{ width: 1, background: palette.border, opacity: 0.7 }}
        />

        {/* Caret secondaire — ouvre "Changer de modèle" */}
        <button
          type="button"
          onClick={handleCaretClick}
          className={`inline-flex items-center justify-center transition-colors ${padCaret}`}
          style={{
            color: palette.text,
            background: open ? palette.hover : "transparent",
          }}
          onMouseEnter={(e) => {
            if (!open) e.currentTarget.style.background = palette.hover;
          }}
          onMouseLeave={(e) => {
            if (!open) e.currentTarget.style.background = "transparent";
          }}
          title="Changer de modèle"
          aria-label="Changer de modèle"
          data-testid={`${baseTestId}-caret`}
        >
          <CaretDown size={caretPx} weight="bold" />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-50 top-full mt-1 right-0 min-w-[240px] max-w-[300px] bg-white border border-slate-200 rounded-xl shadow-xl p-1 overflow-hidden"
          data-testid={`${baseTestId}-menu`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider">
              Changer de modèle — {audience}
            </div>
            {audience === "client" && !dossier?.client_email && (
              <div
                className="text-[10px] text-amber-600 font-semibold mt-0.5"
                data-testid={`${baseTestId}-empty-recipient-warning`}
              >
                ⚠ Email client non renseigné — destinataire vide
              </div>
            )}
          </div>
          {templates.map((tpl, idx) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => handleTemplateClick(tpl)}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
              data-testid={`${baseTestId}-tpl-${tpl.id}`}
            >
              <div className="flex items-center gap-1.5">
                <div className="text-[12px] font-extrabold text-slate-900 group-hover:text-slate-700">
                  {tpl.label}
                </div>
                {idx === 0 && (
                  <span
                    className="px-1 py-0 rounded text-[8px] font-extrabold uppercase tracking-wider"
                    style={{ background: palette.bg, color: palette.text }}
                  >
                    Suggéré
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-500 font-semibold mt-0.5">
                {tpl.description}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
